import { ref, uploadBytes } from 'firebase/storage';
import { CaptureBeginResult, CaptureResult } from '@qkindex/contracts';
import { storage } from '../firebase';
import { RapidCaptureRecord } from '../features/rapidCapture/rapidCaptureTypes';
import { imageOperationService } from './imageOperationService';

export type CaptureStatus = CaptureResult;
const identity = (record: RapidCaptureRecord) => ({
  collectionId: record.collectionId,
  operationId: record.operationId,
  expectedMembershipVersion: record.membershipVersion,
});

export const captureImageHash = async (image: Blob): Promise<string> => {
  const webCrypto = globalThis.crypto;
  if (!webCrypto?.subtle)
    throw Object.assign(
      new Error('Secure photo identification is unavailable in this browser.'),
      { code: 'validation' },
    );
  const bytes =
    typeof image.arrayBuffer === 'function'
      ? await image.arrayBuffer()
      : await new Promise<ArrayBuffer>((resolve, reject) => {
          const reader = new FileReader();
          reader.onload = () => resolve(reader.result as ArrayBuffer);
          reader.onerror = () =>
            reject(new Error('The captured photo could not be read.'));
          reader.readAsArrayBuffer(image);
        });
  const digest = await webCrypto.subtle.digest('SHA-256', bytes);
  return Array.from(new Uint8Array(digest), (byte) =>
    byte.toString(16).padStart(2, '0'),
  ).join('');
};

export const captureService = {
  begin(record: RapidCaptureRecord): Promise<CaptureBeginResult> {
    const needsImage =
      !record.mutation || record.mutation.imageAction === 'replace';
    if (needsImage && !record.imageHash)
      throw new Error('The captured photo has not been identified.');
    const command = {
      action: 'capture-begin' as const,
      ...identity(record),
      itemId: record.itemId,
    };
    if (record.mutation) {
      const mutation = record.mutation;
      return imageOperationService.submit(
        mutation.imageAction === 'replace'
          ? {
              ...command,
              mutation: { ...mutation, imageAction: 'replace' },
              fields: record.mutationFields!,
              contentHash: record.imageHash!,
              imageBytes: record.image.size,
            }
          : {
              ...command,
              mutation: { ...mutation, imageAction: mutation.imageAction },
              fields: record.mutationFields!,
            },
      );
    }
    return imageOperationService.submit({
      ...command,
      contentHash: record.imageHash!,
      imageBytes: record.image.size,
      fields: {
        keywords: record.keywords,
        location: record.location,
        qty: record.qty,
        ...(record.locationId ? { locationId: record.locationId } : {}),
        ...(record.tagIds === undefined ? {} : { tagIds: record.tagIds }),
      },
    });
  },
  async upload(record: RapidCaptureRecord, uploadPath: string): Promise<void> {
    if (
      uploadPath !==
      `collections/${record.collectionId}/uploads/${record.operationId}.jpg`
    )
      throw new Error('The upload destination does not match this capture.');
    await uploadBytes(ref(storage, uploadPath), record.image, {
      contentType: 'image/jpeg',
      customMetadata: {
        captureOperationId: record.operationId,
        contentHash: record.imageHash!,
      },
    });
  },
  commit(record: RapidCaptureRecord): Promise<CaptureStatus> {
    return imageOperationService.submit({
      action: 'capture-commit',
      ...identity(record),
    });
  },
  control(
    record: RapidCaptureRecord,
    phase: 'status' | 'retry' | 'save' | 'discard' | 'analyze',
  ): Promise<CaptureStatus> {
    return imageOperationService.submit({
      action: 'capture-control',
      ...identity(record),
      phase,
      ...((phase === 'retry' || phase === 'analyze') && record.analyzeImage
        ? {
            analysisOperationId: record.analysisOperationId,
            acceptedAiCostVersion: record.acceptedAiCostVersion,
          }
        : {}),
    });
  },
};
